import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { api, ApiError, type PreflightFile } from '@/lib/api'
import { uploadPart } from './uploadPart'

export type Resolution = 'skip' | 'rename' | 'replace'

export type UploadStatus =
  | 'conflict' // waiting for the user to choose skip / keep both / replace
  | 'queued'
  | 'uploading'
  | 'completed'
  | 'failed'
  | 'cancelled'

export interface UploadItem {
  id: string
  file: File
  bucket: string
  prefix: string
  key: string
  status: UploadStatus
  conflict: PreflightFile | null
  resolution: Resolution | null
  uploadedBytes: number
  totalBytes: number
  error: string | null
  /** Bytes per second, smoothed over a short window. */
  rateBps: number
  finalKey: string | null
  /** Raw X-HCP-RETENTION value chosen when this file was queued. */
  retention: string
  /** Whether this upload was queued with "make public" ticked. */
  makePublic: boolean
  /** Permanent anonymous URL, available once the upload completes. */
  publicUrl: string | null
  /** Set when the upload succeeded but the ACL could not be applied. */
  publicError: string | null
}

interface UploadState {
  items: UploadItem[]
  activeCount: number
  hasConflicts: boolean
  addFiles: (
    files: File[],
    bucket: string,
    prefix: string,
    retention?: string,
    makePublic?: boolean,
  ) => Promise<void>
  resolve: (id: string, resolution: Resolution) => void
  resolveAll: (resolution: Resolution) => void
  cancel: (id: string) => void
  retry: (id: string) => void
  dismiss: (id: string) => void
  clearFinished: () => void
  /** Incremented whenever an upload completes, so listings can refresh. */
  completionTick: number
}

const UploadContext = createContext<UploadState | null>(null)

const PART_CONCURRENCY = 3
const MAX_PART_ATTEMPTS = 3
const RESUME_STORAGE_KEY = 'hcp-upload-sessions'

/** Identifies a specific file targeted at a specific key, for resume lookup. */
function fingerprint(item: UploadItem): string {
  return [item.bucket, item.key, item.file.size, item.file.lastModified].join('|')
}

function readResumeMap(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(RESUME_STORAGE_KEY) ?? '{}') as Record<string, string>
  } catch {
    return {}
  }
}

function writeResumeEntry(key: string, uploadId: string | null): void {
  try {
    const map = readResumeMap()
    if (uploadId) map[key] = uploadId
    else delete map[key]
    localStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify(map))
  } catch {
    // Storage unavailable — resume is a convenience, not a requirement.
  }
}

export function UploadProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<UploadItem[]>([])
  const [completionTick, setCompletionTick] = useState(0)

  // Mirrors `items` so the worker loop can read current state without being
  // re-created on every render.
  const itemsRef = useRef<UploadItem[]>([])
  itemsRef.current = items

  const controllers = useRef(new Map<string, AbortController>())
  const running = useRef(false)

  const patch = useCallback((id: string, changes: Partial<UploadItem>) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...changes } : item)),
    )
  }, [])

  // --- queueing -----------------------------------------------------------

  const addFiles = useCallback(
    async (files: File[], bucket: string, prefix: string, retention = '', makePublic = false) => {
    if (files.length === 0) return

    let preflight: PreflightFile[] = []
    try {
      const res = await api.preflight(
        bucket,
        prefix,
        files.map((f) => ({ name: f.name, size: f.size })),
      )
      preflight = res.files
    } catch (err) {
      // Without preflight we cannot tell which files would collide, and on HCP
      // a colliding write fails outright. Surfacing the error is better than
      // queueing uploads that are certain to be rejected.
      const message = err instanceof ApiError ? err.message : 'Could not check for existing files'
      setItems((current) => [
        ...current,
        ...files.map((file, index) => ({
          id: `${Date.now()}-${index}-${file.name}`,
          file,
          bucket,
          prefix,
          key: prefix + file.name,
          status: 'failed' as const,
          conflict: null,
          resolution: null,
          uploadedBytes: 0,
          totalBytes: file.size,
          error: message,
          rateBps: 0,
          finalKey: null,
          retention,
          makePublic,
          publicUrl: null,
          publicError: null,
        })),
      ])
      return
    }

    const byName = new Map(preflight.map((p) => [p.name, p]))

    setItems((current) => [
      ...current,
      ...files.map((file, index): UploadItem => {
        const check = byName.get(file.name)
        return {
          id: `${Date.now()}-${index}-${file.name}`,
          file,
          bucket,
          prefix,
          key: check?.key ?? prefix + file.name,
          status: check?.exists ? 'conflict' : 'queued',
          conflict: check?.exists ? check : null,
          resolution: null,
          uploadedBytes: 0,
          totalBytes: file.size,
          error: null,
          rateBps: 0,
          finalKey: null,
          retention,
          makePublic,
          publicUrl: null,
          publicError: null,
        }
      }),
    ])
    },
    [],
  )

  const resolve = useCallback(
    (id: string, resolution: Resolution) => {
      if (resolution === 'skip') {
        setItems((current) => current.filter((item) => item.id !== id))
        return
      }
      patch(id, { resolution, status: 'queued' })
    },
    [patch],
  )

  const resolveAll = useCallback(
    (resolution: Resolution) => {
      if (resolution === 'skip') {
        setItems((current) => current.filter((item) => item.status !== 'conflict'))
        return
      }
      setItems((current) =>
        current.map((item) =>
          item.status === 'conflict' && !(resolution === 'replace' && item.conflict?.locked)
            ? { ...item, resolution, status: 'queued' }
            : item,
        ),
      )
    },
    [],
  )

  const cancel = useCallback(
    (id: string) => {
      controllers.current.get(id)?.abort()
      patch(id, { status: 'cancelled', rateBps: 0 })
    },
    [patch],
  )

  const retry = useCallback(
    (id: string) => {
      patch(id, { status: 'queued', error: null, uploadedBytes: 0, rateBps: 0 })
    },
    [patch],
  )

  const dismiss = useCallback((id: string) => {
    setItems((current) => current.filter((item) => item.id !== id))
  }, [])

  const clearFinished = useCallback(() => {
    setItems((current) =>
      current.filter(
        (item) => item.status !== 'completed' && item.status !== 'cancelled' && item.status !== 'failed',
      ),
    )
  }, [])

  // --- worker -------------------------------------------------------------

  const runOne = useCallback(
    async (item: UploadItem) => {
      const controller = new AbortController()
      controllers.current.set(item.id, controller)
      patch(item.id, { status: 'uploading', uploadedBytes: 0, error: null })

      const fp = fingerprint(item)

      try {
        // Reuse a still-open server-side session if this exact file was being
        // uploaded before a refresh, so a large transfer does not restart.
        let session: {
          id: string
          key: string
          partSize: number
          totalParts: number
        } | null = null
        let alreadyUploaded = new Set<number>()

        const savedId = readResumeMap()[fp]
        if (savedId) {
          try {
            const existing = await api.getUpload(savedId)
            if (existing.status === 'PENDING' && existing.size === item.file.size) {
              session = {
                id: existing.id,
                key: existing.key,
                partSize: existing.partSize,
                totalParts: existing.totalParts,
              }
              alreadyUploaded = new Set(existing.uploadedParts)
            } else {
              writeResumeEntry(fp, null)
            }
          } catch {
            writeResumeEntry(fp, null)
          }
        }

        if (!session) {
          const created = await api.createUpload({
            bucket: item.bucket,
            key: item.key,
            size: item.file.size,
            contentType: item.file.type || 'application/octet-stream',
            onConflict: item.resolution === 'replace' ? 'replace' : item.resolution === 'rename' ? 'rename' : 'fail',
            ...(item.retention ? { retention: item.retention } : {}),
            ...(item.makePublic ? { makePublic: true } : {}),
          })
          session = created
          writeResumeEntry(fp, created.id)
        }

        const { id: uploadId, partSize, totalParts } = session

        // Progress is assembled from per-part byte counts so that concurrent
        // parts do not fight over a single counter.
        const partProgress = new Map<number, number>()
        for (const partNumber of alreadyUploaded) {
          partProgress.set(partNumber, partSizeFor(partNumber, partSize, item.file.size))
        }

        const startedAt = performance.now()
        let lastReport = 0

        const reportProgress = () => {
          const uploaded = [...partProgress.values()].reduce((sum, n) => sum + n, 0)
          const now = performance.now()
          // Throttled: a 2 GB upload fires thousands of progress events, and
          // re-rendering on each one is wasted work.
          if (now - lastReport < 100 && uploaded < item.file.size) return
          lastReport = now
          const elapsedSec = (now - startedAt) / 1000
          patch(item.id, {
            uploadedBytes: uploaded,
            rateBps: elapsedSec > 0.5 ? uploaded / elapsedSec : 0,
          })
        }

        const pending = Array.from({ length: totalParts }, (_, i) => i + 1).filter(
          (n) => !alreadyUploaded.has(n),
        )

        let cursor = 0
        const worker = async (): Promise<void> => {
          while (cursor < pending.length) {
            if (controller.signal.aborted) return
            const partNumber = pending[cursor++] as number
            const start = (partNumber - 1) * partSize
            const blob = item.file.slice(start, Math.min(start + partSize, item.file.size))

            let attempt = 0
            for (;;) {
              attempt += 1
              try {
                await uploadPart({
                  uploadId,
                  partNumber,
                  blob,
                  signal: controller.signal,
                  onProgress: (sent) => {
                    partProgress.set(partNumber, sent)
                    reportProgress()
                  },
                })
                partProgress.set(partNumber, blob.size)
                reportProgress()
                break
              } catch (err) {
                if (controller.signal.aborted) return
                // Only transient failures are worth retrying; a 4xx means the
                // request itself is wrong and will fail again identically.
                const transient =
                  !(err instanceof ApiError) || err.status === 0 || err.status >= 500
                if (!transient || attempt >= MAX_PART_ATTEMPTS) throw err
                partProgress.set(partNumber, 0)
                await new Promise((r) => setTimeout(r, 500 * attempt))
              }
            }
          }
        }

        await Promise.all(
          Array.from({ length: Math.min(PART_CONCURRENCY, pending.length || 1) }, worker),
        )

        if (controller.signal.aborted) {
          await api.abortUpload(uploadId).catch(() => {})
          writeResumeEntry(fp, null)
          return
        }

        const result = await api.completeUpload(uploadId)
        writeResumeEntry(fp, null)

        patch(item.id, {
          status: 'completed',
          uploadedBytes: item.file.size,
          rateBps: 0,
          finalKey: result.key,
          publicUrl: result.publicUrl,
          publicError: result.publicError,
        })
        setCompletionTick((tick) => tick + 1)
      } catch (err) {
        if (controller.signal.aborted || (err as Error)?.name === 'AbortError') {
          patch(item.id, { status: 'cancelled', rateBps: 0 })
          return
        }
        patch(item.id, {
          status: 'failed',
          rateBps: 0,
          error: err instanceof ApiError ? err.message : 'Upload failed',
        })
      } finally {
        controllers.current.delete(item.id)
      }
    },
    [patch],
  )

  // One file at a time. Parts within a file already run concurrently, so
  // uploading several files at once would compete for the same bandwidth
  // without finishing anything sooner.
  useEffect(() => {
    if (running.current) return
    const next = items.find((item) => item.status === 'queued')
    if (!next) return

    running.current = true
    void runOne(next).finally(() => {
      running.current = false
      // Nudges this effect to look for the next queued item.
      setItems((current) => [...current])
    })
  }, [items, runOne])

  // Warns before a refresh discards in-flight uploads.
  useEffect(() => {
    const active = items.some((item) => item.status === 'uploading')
    if (!active) return

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [items])

  const value = useMemo<UploadState>(
    () => ({
      items,
      activeCount: items.filter((i) => i.status === 'uploading' || i.status === 'queued').length,
      hasConflicts: items.some((i) => i.status === 'conflict'),
      addFiles,
      resolve,
      resolveAll,
      cancel,
      retry,
      dismiss,
      clearFinished,
      completionTick,
    }),
    [items, addFiles, resolve, resolveAll, cancel, retry, dismiss, clearFinished, completionTick],
  )

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
}

function partSizeFor(partNumber: number, partSize: number, totalSize: number): number {
  const start = (partNumber - 1) * partSize
  return Math.min(partSize, Math.max(0, totalSize - start))
}

export function useUploads(): UploadState {
  const context = useContext(UploadContext)
  if (!context) throw new Error('useUploads must be used inside an UploadProvider')
  return context
}
